<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import CodeInput from '@kigaroo/components/compound/code-input/CodeInput.vue';
import { SphSpinner } from '@kigaroo/components/base/spinner';

export interface LoginUserToken {
    token: string
    name: string
    institutionName: string
    institutionId: number
    federationId?: number
    contractType?: 'regular' | 'passive' | 'guest'
}

const props = defineProps<{
    twoFactorToken: string
}>();
const emit = defineEmits<{
    success: [tokens: LoginUserToken[]]
    cancel: []
}>();
const TRUSTED_DEVICE_TOKEN_KEY = 'kgr_trusted_device_token';
const TIMEOUT_MS = 3 * 60 * 1000;

const code = ref<string | null>(null);
const trustedDevice = ref(false);
const useBackupCode = ref(false);
const isLoading = ref(false);
const errorInvalidCode = ref(false);
const errorOffline = ref(false);
const errorTooManyAttempts = ref(false);
const errorUnexpected = ref(false);

const codeLength = computed(() => (useBackupCode.value ? 8 : 6));
const codePattern = computed(() => (useBackupCode.value ? '[a-zA-Z0-9]' : '[0-9]*'));

function clearErrors() {
    errorInvalidCode.value = false;
    errorOffline.value = false;
    errorTooManyAttempts.value = false;
    errorUnexpected.value = false;
}

function onToggleBackupCode() {
    useBackupCode.value = !useBackupCode.value;
    code.value = null;
    clearErrors();
}

async function onCodeFilled(value: string | null) {
    if (!value || isLoading.value) {
        return;
    }
    clearErrors();
    isLoading.value = true;
    try {
        const { apiHost } = window.kgrDynamicConfig;
        const response = await fetch(`${apiHost}/api/2fa/verify-app`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${props.twoFactorToken}`,
            },
            body: JSON.stringify({ code: value, trustedDevice: trustedDevice.value }),
        });
        if (response.status === 400) {
            errorInvalidCode.value = true;
            code.value = null;
            return;
        }
        if (response.status === 429) {
            errorTooManyAttempts.value = true;
            return;
        }
        if (!response.ok) {
            errorUnexpected.value = true;
            return;
        }
        const {
            data: { tokens, trusted_device_token },
        } = await response.json();
        if (trusted_device_token) {
            localStorage.setItem(TRUSTED_DEVICE_TOKEN_KEY, trusted_device_token);
        }
        emit('success', tokens);
    } catch {
        errorOffline.value = true;
    } finally {
        isLoading.value = false;
    }
}

let logoutTimer: ReturnType<typeof setTimeout> | null = null;
onMounted(() => {
    logoutTimer = setTimeout(() => emit('cancel'), TIMEOUT_MS);
});
onUnmounted(() => {
    if (logoutTimer) {
        clearTimeout(logoutTimer);
    }
});
</script>

<template>
    <div>
        <p class="kgr-text kgr-row">
            <strong>{{ $t('Zwei-Faktor-Authentifizierung') }}</strong><br />
            {{ useBackupCode ? $t('Verifizierung mit Backup-Code') : $t('6-stelligen Code aus der Authenticator-App eingeben.') }}
        </p>

        <div
            v-if="errorInvalidCode"
            class="kgr-alert kgr-alert--error kgr-row"
        >
            {{ $t('Der eingegebene Code ist ungültig.') }}
        </div>
        <div
            v-if="errorTooManyAttempts"
            class="kgr-alert kgr-alert--error kgr-row"
        >
            {{ $t('Zu viele fehlgeschlagene Versuche. Bitte warte einen Moment und versuche es erneut.') }}
        </div>
        <div
            v-if="errorOffline"
            class="kgr-alert kgr-alert--error kgr-row"
        >
            {{ $t('Es konnte keine Verbindung zum Server hergestellt werden.') }}
        </div>
        <div
            v-if="errorUnexpected"
            class="kgr-alert kgr-alert--error kgr-row"
        >
            {{ $t('Es ist leider ein unerwarteter Fehler aufgetreten.') }}
        </div>

        <div class="kgr-row kgr-text kgr-text--centered">
            <div
                v-if="isLoading"
                class="kgr-row kgr-text kgr-text--centered"
            >
                <SphSpinner size="lg" />
            </div>
            <CodeInput
                v-else
                v-model:model-value="code"
                :length="codeLength"
                :pattern="codePattern"
                inputmode="decimal"
                @update:model-value="onCodeFilled"
            />
        </div>

        <div
            class="kgr-row kgr-buttonGroup"
            style="display: flex; gap: 1rem"
        >
            <button
                type="button"
                class="kgr-button kgr-button--ghost"
                @click="onToggleBackupCode"
            >
                {{ useBackupCode ? $t('Per Code einloggen') : $t('Verifizierung mit Backup-Code') }}
            </button>
            <button
                type="button"
                class="kgr-link kgr-clickable"
                @click="emit('cancel')"
            >
                {{ $t('Abbrechen') }}
            </button>
        </div>
    </div>
</template>
